import { useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useDiscardGuard } from '@/components/ui/form-dialog-content'
import { TareaForm, type TareaFormValues } from './TareaForm'
import { AdjuntosPendientes } from './AdjuntosPendientes'
import { TareaAdjuntos } from '@/components/tareas/TareaAdjuntos'
import type { TareaCompleta } from '@/types/vencimientos'

interface TareaFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tarea?: TareaCompleta | null
  onSubmit: (data: TareaFormValues, archivos?: File[]) => void
  isLoading?: boolean
  // ORR-148: habilita el selector de adjuntos durante la creación (upload diferido).
  // Lo controla el caller según el permiso `subirDocumento`.
  canAttach?: boolean
  /** ORR-327: valores iniciales al crear (ej. el cliente desde cuya ficha se abrió). */
  defaultValues?: Partial<TareaFormValues>
  /** Ofrecer "Repetir esta tarea". Solo para callers que procesen `repetir` en el submit. */
  canRepeat?: boolean
}

export function TareaFormDialog({
  open,
  onOpenChange,
  tarea,
  onSubmit,
  isLoading,
  canAttach = false,
  defaultValues,
  canRepeat = false,
}: TareaFormDialogProps) {
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])

  const isEditing = !!tarea

  const { holdClose, confirmation } = useDiscardGuard({
    isDirty: isFormDirty || pendingFiles.length > 0,
    onDiscard: () => {
      setPendingFiles([])
      onOpenChange(false)
    },
  })

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && holdClose()) return
    onOpenChange(newOpen)
  }

  const handleSubmit = (data: TareaFormValues) => {
    onSubmit(data, pendingFiles)
    // Limpiar la selección tras delegar el submit. El array ya fue capturado por
    // el caller; este setState crea uno nuevo y no muta el que se pasó.
    setPendingFiles([])
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {isEditing ? 'Editar Tarea' : 'Nueva Tarea'}
            </SheetTitle>
            <SheetDescription>
              {isEditing
                ? 'Modifica los datos de la tarea'
                : 'Completa los datos para crear una nueva tarea'}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            {/* Adjuntar archivos durante la creación (upload diferido, ORR-148).
                Va antes del form porque el TareaForm de core lleva sus botones
                adentro. En edición los adjuntos se gestionan desde el detalle. */}
            {!isEditing && canAttach && (
              <div className="mb-4 pb-4 border-b">
                <AdjuntosPendientes files={pendingFiles} onChange={setPendingFiles} />
              </div>
            )}

            {/* En edición la tarea ya existe: gestión real de adjuntos (subir/ver/eliminar). */}
            {isEditing && canAttach && tarea?.id && (
              <div className="mb-4 pb-4 border-b">
                <TareaAdjuntos tareaId={tarea.id} clienteId={tarea.cliente_id} />
              </div>
            )}

            <TareaForm
              tarea={tarea}
              onSubmit={handleSubmit}
              onCancel={() => handleOpenChange(false)}
              onDirtyChange={setIsFormDirty}
              isLoading={isLoading}
              defaultValues={defaultValues}
              canRepeat={canRepeat}
            />
          </div>
        </SheetContent>
      </Sheet>

      {confirmation}
    </>
  )
}
